import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { storageService } from "../../services/storageService";

const API_URL = process.env.REACT_APP_V5_API_URL ?? "";

interface FailedRequest {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((req) => {
    if (token) req.resolve(token);
    else req.reject(error);
  });

  failedQueue = [];
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    const token = storageService.getAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  },
});

/**
 * Fully typed version of your Axios interceptors inside RTK Query.
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  const error = result.error;
  const meta = result.meta as FetchBaseQueryMeta | undefined;

  const status = error?.status;
  const responseUrl = meta?.response?.url;

  // ---------------------
  // MULTI-DEVICE 401 HANDLING
  // ---------------------
  const isMultiDeviceError =
    responseUrl?.includes("accessCode") && status === 401;

  if (isMultiDeviceError) {
    return result;
  }

  // ---------------------
  // 401 (Token Expired) — Refresh Token Logic
  // ---------------------
  if (status === 401) {
    const originalRequest = args as FetchArgs & { _retry?: boolean };

    if (!originalRequest._retry) {
      originalRequest._retry = true;

      // If refresh already in progress → queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (!originalRequest.headers) originalRequest.headers = {};
              (originalRequest.headers as Record<string, string>)[
                "Authorization"
              ] = `Bearer ${token}`;

              resolve(rawBaseQuery(originalRequest, api, extraOptions));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = storageService.getRefreshToken();

        const refreshResponse = await fetch(
          `${API_URL}/auth/refresh/refresh-tokens`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken }),
          }
        ).then((r) => r.json());

        const newAccessToken = refreshResponse.data.access.token;
        const newRefreshToken = refreshResponse.data.refresh.token;

        // Save tokens
        storageService.setAccessToken(newAccessToken);
        storageService.setRefreshToken(newRefreshToken);

        // Resolve queued requests
        processQueue(null, newAccessToken);

        // Retry original request
        if (!originalRequest.headers) originalRequest.headers = {};
        (originalRequest.headers as Record<string, string>)["Authorization"] = `Bearer ${newAccessToken}`;


        return await rawBaseQuery(originalRequest, api, extraOptions);
      } catch (err) {
        processQueue(err, null);
        storageService.clearAll();
        window.location.href = "/auth/login";
        return result;
      } finally {
        isRefreshing = false;
      }
    }
  }

  // ---------------------
  // 402 → FORCE LOGOUT
  // ---------------------
  if (status === 402) {
    storageService.clearAll();
    window.location.href = "/auth/login";
  }

  return result;
};
